import {QueuedMessageIcon} from '@github-ui/custom-icons/QueuedMessageIcon'
import {announce} from '@github-ui/aria-live'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {useRuntimeEnvironment} from '@github-ui/ssr-utils/use-runtime-environment'
import {useDynamicTextareaHeight} from '@github-ui/use-dynamic-textarea-height'
import {isMacOS} from '@github-ui/get-os'
import {
  AlertIcon,
  EyeClosedIcon,
  EyeIcon,
  FileCodeIcon,
  FileIcon,
  KebabHorizontalIcon,
  PaperAirplaneIcon,
  PlusIcon,
  RepoIcon,
  SquareFillIcon,
  SyncIcon,
  UploadIcon,
  XIcon,
} from '@primer/octicons-react'

import type {AnchorSide} from '@primer/behaviors'
import {ActionList, ActionMenu, IconButton, useMergedRefs, VisuallyHidden} from '@primer/react'
import {clsx} from 'clsx'
import type {ReactElement, ReactNode} from 'react'
import {useCallback, useEffect, useMemo, useRef, useState} from 'react'

import styles from './ChatComposer.module.css'
import {useAttachmentOverflow} from '../hooks/use-attachment-overflow'
import {useChatComposerReadiness, type ChatComposerReadiness} from '../hooks/use-chat-composer-readiness'
import {useChatComposerPresentation, type ChatComposerPresentation} from '../hooks/use-chat-composer-presentation'
import {
  useChatComposerInteractions,
  type ChatComposerInteractionObserver,
  type ComposerCommandKind,
} from '../hooks/use-chat-composer-interactions'
import {
  ATTACHMENT_ICON_COLORS,
  ATTACHMENT_ICON_STATUS_LABELS,
  ATTACHMENT_ICONS,
  type ComposerAttachmentIcon,
} from './chat-composer-attachments'

const MAX_HEIGHT = 300
// Matches --composer-min-height in ChatComposer.module.css: base-size-16 * 2 + base-size-24
const MIN_HEIGHT = 56

function supportsFieldSizingContent(): boolean {
  try {
    return CSS.supports('field-sizing', 'content')
  } catch {
    return false
  }
}

/** A single attachment token displayed in the composer. */
export interface ComposerAttachment {
  id: string
  label: string
  icon?: ComposerAttachmentIcon
  /** When set, the token renders as a link opening this URL in a new tab. */
  url?: string
  /** Set to false when the attachment cannot be removed. Defaults to true. */
  removable?: boolean
  /**
   * Sandbox upload state. Omitted for attachments that are not being written to the
   * session's filesystem, which keeps every existing caller unchanged.
   */
  upload?: ComposerAttachmentUpload
}

/** Progress of writing an attachment into the session's sandbox filesystem. */
export interface ComposerAttachmentUpload {
  status: 'uploading' | 'failed'
  /** 0–100. Omitted when the total size is unknown. */
  percent?: number
  /** Why the upload failed, shown as the token's tooltip. */
  error?: string
  /** Present only when the failure is worth another attempt. */
  onRetry?: () => void
}

/**
 * An image attached to a composer message. `dataUrl` is a fully-qualified
 * `data:` URL used for inline previews, while `data` is the bare base64 payload
 * (no `data:` prefix) suitable for sending over the wire as an embedded blob.
 */
export interface ComposerImage {
  /** Stable id for React keys and removal. */
  id: string
  /** Human-readable label shown on the preview chip (e.g. a filename). */
  name: string
  /** `data:<mime>;base64,<...>` URL for rendering an inline preview. */
  dataUrl: string
  /** MIME type, e.g. `image/png`. */
  contentType: string
  /** Base64-encoded image bytes without the `data:` prefix. */
  data: string
}

interface ChatComposerBaseProps {
  interactionObserver?: ChatComposerInteractionObserver
  onReadiness?: (readiness: ChatComposerReadiness) => void
  onPresentation?: (controls: ChatComposerPresentation) => (() => void) | undefined
  /** Forwarded to the underlying textarea */
  textAreaRef?: React.Ref<HTMLTextAreaElement>
  submitButtonRef?: React.Ref<HTMLButtonElement>
  /** Placeholder text shown when the input is empty. */
  placeholder?: string
  /** Whether a response is currently being generated. Controls stop/submit button state. */
  isStreaming?: boolean
  /** Called when the user submits. Return false when skipped so the optimistically cleared draft is restored. */
  onSubmit?: ChatComposerSubmitHandler
  onSubmitInteraction?: (prompt: string, event?: Event) => boolean | void | Promise<boolean | void>
  /** Called when the user queues. Return false when skipped so the optimistically cleared draft is restored. */
  onQueueSubmit?: ChatComposerSubmitHandler
  onQueueSubmitInteraction?: (prompt: string, event?: Event) => boolean | void | Promise<boolean | void>
  /** Called synchronously before `onSubmit` or `onQueueSubmit` with the gesture that triggered it, for telemetry. */
  onSubmitGesture?: (method: ChatComposerSubmitMethod) => void
  /** When true, the platform modifier plus Enter queues through `onQueueSubmit` while streaming. */
  queueOnModifierEnter?: boolean
  /** Called when the user cancels the current generation. */
  onStop?: () => void
  onStopInteraction?: (event?: Event) => void
  /** Accessible label for the explicit queue action shown while the platform modifier is held. */
  queueSubmitAriaLabel?: string
  /** When true, Enter and the submit button use `onSubmit` while `isStreaming` is true. */
  submitWhileStreaming?: boolean
  /** Accessible label for the normal submit button shown while streaming. */
  submitWhileStreamingAriaLabel?: string
  /** Tooltip description for the submit button. */
  submitDescription?: string
  /** Disables auto-focusing the input on mount. */
  disableAutoFocus?: boolean
  /** Accessible label for the textarea. Falls back to placeholder if not provided. */
  ariaLabel?: string
  /** IDs of elements that describe the textarea. */
  ariaDescribedBy?: string
  /** Content rendered on the leading (left) side of the toolbar, e.g. a model picker. */
  toolbarContent?: ReactNode
  /** Content rendered on the trailing (right) side of the toolbar, before the send button, e.g. a model picker. */
  trailingToolbarContent?: ReactNode
  /** Whether to render a separator between trailing toolbar content and the send button. Defaults to true. */
  showTrailingToolbarSeparator?: boolean
  /** When true, disables submission independently of `isStreaming`. */
  disabled?: boolean
  /** Disables the normal submit action without disabling the explicit queue action. */
  submitDisabled?: boolean
  preventSubmitKeysWhenDisabled?: boolean
  onBlockedSubmit?: () => void
  /** Disables the explicit queue action without disabling the normal submit action. */
  queueSubmitDisabled?: boolean
  /**
   * When true, disables and dims the composer while an awaited submit is in
   * flight, swapping the send button for a spinner. Defaults to false.
   */
  disableWhileSubmitting?: boolean
  /** Maximum number of characters allowed in the textarea. Applied as the native `maxLength`. */
  maxLength?: number
  /** Maximum height for the scrollable input wrapper in pixels. The textarea itself remains uncapped. */
  maxTextareaHeight?: number
  /** When true, a plain Enter inserts a newline and submission requires Cmd/Ctrl+Enter. Defaults to false. */
  submitWithModifier?: boolean
  /** When false, the textarea keeps its text after a successful submit instead of clearing. Defaults to true. */
  clearOnSubmit?: boolean
  /** When true, keeps the textarea visible but prevents editing and attachment changes. */
  readOnly?: boolean
  /**
   * When omitted, follows the `copilot_css_textarea_autosize` feature flag on browsers that support
   * `field-sizing: content`, and uses legacy JS autosize elsewhere. Pass true to force CSS autosize (with a
   * measured fallback), or false to force legacy JS autosize in tests or stories.
   */
  cssAutosize?: boolean
  /**
   * Content rendered above the textarea, inside the composer frame (e.g. pasted-code
   * blocks). Lets a consumer attach extra UI without the composer knowing about it.
   */
  aboveInput?: ReactNode
  /**
   * When true, submission is allowed even when the textarea is empty, e.g. because
   * `aboveInput` holds content that will be submitted. Defaults to false.
   */
  hasAttachedContent?: boolean
  /**
   * Called on every change to the textarea value, including whitespace-only
   * edits. Fires with no payload so a consumer can react to compose *intent*
   * (e.g. warm a backend) without coupling to the draft text. Not called on
   * focus or when opening menus.
   */
  onInputMutation?: () => void
  /** Forwarded to the textarea's paste event so a consumer can intercept pastes. */
  onPaste?: React.ClipboardEventHandler<HTMLTextAreaElement>
  /**
   * Called when Backspace is pressed while the textarea is empty, e.g. to remove
   * the last item rendered in `aboveInput` (like a pasted code block).
   */
  onEmptyBackspace?: () => void
  /** Attachment tokens to display above the textarea. */
  attachments?: ComposerAttachment[]
  /**
   * Rendered above the textarea, after any attachment tokens. Lets a consumer
   * render its own attachment previews (e.g. image thumbnails). When present,
   * submission is allowed even if the text box is empty.
   */
  attachmentPreview?: ReactNode
  /**
   * Called with the raw files attached via paste or drop, along with the
   * `source` of the files. When provided, paste handling is enabled. Drop
   * handling is delegated to the consuming surface (e.g. via an ancestor
   * `FileAttachment` that calls the composer's imperative handle); this
   * callback is still invoked with `source: 'drop'` in that case. When
   * omitted, paste handling is disabled.
   */
  onFilesAdded?: (files: File[], source: 'paste' | 'drop') => void
  /** Called when the user selects "Files and folders" from the attach menu. */
  onAttachFiles?: () => void
  /** Called when the user selects "Upload from computer" from the attach menu. */
  onUploadFiles?: () => void
  /** Called when the user selects "Repository" from the attach menu. */
  onAttachRepo?: () => void
  /** Keeps a disabled attach button visible when the composer is read-only. */
  showAttachButtonWhenReadOnly?: boolean
  /** Ref for the visible attach-menu trigger, used by controlled attachment pickers to restore focus. */
  attachButtonRef?: React.RefObject<HTMLButtonElement | null>
  /**
   * Extra content rendered inside the attach ("+") menu's `ActionList`, after the
   * built-in items. Lets a consumer add its own entries (e.g. a Space submenu)
   * without the composer knowing about them. When provided, the attach menu is
   * shown even if none of the built-in attach handlers are set.
   */
  attachMenuContent?: ReactNode
  /** Called when the user removes an attachment token. */
  onRemoveAttachment?: (id: string) => void
  /** Optional activation, independent of removal. Call preventDefault when handling a URL-backed token. */
  onAttachmentClick?: (attachment: ComposerAttachment, event: React.MouseEvent<HTMLElement>) => void
  /** Controls which side of the anchor the attach menu overlay opens on. */
  attachMenuSide?: AnchorSide
  /** Portal container name for overlays (keeps them inside a fixed container). */
  portalContainerName?: string
  /**
   * Optional wrapper for the underlying `<textarea>` element. When provided, the
   * function receives the bare textarea ReactElement and must return a wrapped
   * version (e.g. an `InlineAutocomplete`). This lets consumers add autocomplete
   * behaviour without coupling `agent-sessions-interface` to autocomplete or data
   * concerns. The wrapper is responsible for forwarding all props it receives to
   * the textarea so keyboard, focus, and aria contracts are preserved.
   */
  renderInput?: (textarea: ReactElement) => ReactElement
  /** Marks the textarea as the focus target after client-side navigation. */
  autoFocusOnNavigation?: boolean
  /** Optional class applied to the composer's outermost container. */
  containerClassName?: string
  /**
   * Controls textarea text sizing. `'large'` (default) keeps a fixed large font;
   * `'responsive'` uses large on mobile and medium on wider viewports (≥768px).
   */
  inputTextSize?: 'large' | 'responsive'
  /**
   * Anchor side for the send/stop button tooltips. Surfaces that render the composer
   * against the bottom of a constrained container (e.g. a side panel) need `'n'` so
   * the tooltip isn't clipped.
   */
  tooltipDirection?: 'n' | 's' | 'e' | 'w' | 'ne' | 'nw' | 'se' | 'sw'
  /** Enables shell-command mode when the value starts with `!`. */
  supportsShellCommands?: boolean
}

export type ChatComposerValueChangeReason = 'input' | 'submit-clear' | 'submit-restore'

/** The gesture that triggered a submit: Enter, the platform modifier plus Enter, or the send button. */
export type ChatComposerSubmitMethod = 'enter' | 'modifier_enter' | 'button'
type ChatComposerSubmitHandler = (prompt: string) => boolean | void | Promise<boolean | void>

/**
 * Controlled vs. uncontrolled text ownership, modeled as a union so `value` and
 * `onValueChange` must be supplied together or not at all. In controlled mode
 * the parent owns the draft (letting it survive the composer unmounting); pass
 * an updater-capable setter (e.g. a `useState` dispatcher) so functional updates
 * resolve against the parent's current state and an async continuation (like the
 * restore-on-failure clear) can't clobber a newer draft. When omitted, the
 * composer manages its own text state.
 */
type ChatComposerTextProps =
  | {
      value: string
      onValueChange: (value: string | ((current: string) => string), reason?: ChatComposerValueChangeReason) => void
    }
  | {value?: undefined; onValueChange?: undefined}

export type ChatComposerProps = ChatComposerBaseProps & ChatComposerTextProps

export function ChatComposer({
  onReadiness,
  onPresentation,
  textAreaRef: forwardedTextAreaRef,
  placeholder = 'Ask anything. Type / for commands, @ to add references',
  value,
  interactionObserver,
  onValueChange,
  isStreaming = false,
  onSubmit,
  onSubmitInteraction,
  onQueueSubmit,
  onQueueSubmitInteraction,
  onSubmitGesture,
  submitButtonRef,
  queueOnModifierEnter = false,
  onStop,
  onStopInteraction,
  queueSubmitAriaLabel = 'Queue message',
  submitWhileStreaming = false,
  submitWhileStreamingAriaLabel = 'Send message',
  submitDescription,
  disableAutoFocus = false,
  ariaLabel,
  ariaDescribedBy,
  toolbarContent,
  trailingToolbarContent,
  showTrailingToolbarSeparator = true,
  disabled = false,
  submitDisabled = false,
  preventSubmitKeysWhenDisabled = false,
  onBlockedSubmit,
  queueSubmitDisabled = false,
  disableWhileSubmitting = false,
  maxLength,
  maxTextareaHeight,
  submitWithModifier = false,
  clearOnSubmit = true,
  readOnly = false,
  cssAutosize: cssAutosizeProp,
  aboveInput,
  hasAttachedContent = false,
  onPaste,
  onEmptyBackspace,
  onInputMutation,
  attachments,
  attachmentPreview,
  onFilesAdded,
  onAttachFiles,
  onUploadFiles,
  onAttachRepo,
  showAttachButtonWhenReadOnly = false,
  attachButtonRef,
  attachMenuContent,
  onRemoveAttachment,
  onAttachmentClick,
  attachMenuSide,
  portalContainerName,
  renderInput,
  autoFocusOnNavigation = false,
  containerClassName,
  inputTextSize = 'large',
  tooltipDirection,
  supportsShellCommands = false,
}: ChatComposerProps) {
  const {isBrowser} = useRuntimeEnvironment()
  // Without native `field-sizing`, the CSS path's measured fallback grows but never shrinks a nonempty draft,
  // so the flag default keeps those browsers on the legacy path. The server assumes support.
  const canCssAutosize = useMemo(() => !isBrowser || supportsFieldSizingContent(), [isBrowser])
  const cssAutosize = cssAutosizeProp ?? (canCssAutosize && isFeatureEnabled('copilot_css_textarea_autosize'))
  const [internalText, setInternalText] = useState('')
  // Controlled when `value` is supplied; otherwise the composer owns its text.
  const isControlled = value !== undefined
  const text = isControlled ? value : internalText
  const isShellMode = supportsShellCommands && text.startsWith('!')
  const shellCommandText = isShellMode ? text.slice(1) : text
  const setText = useCallback(
    (update: string | ((current: string) => string), reason: ChatComposerValueChangeReason = 'input') => {
      if (isControlled) {
        // Forward the update (string or updater) straight to the parent so a
        // functional update resolves against the parent's *current* state. The
        // parent (a `useState` dispatcher) owns the value, so this stays correct
        // even when the composer that started an async submit has unmounted and
        // remounted before the continuation runs.
        onValueChange?.(update, reason)
      } else {
        setInternalText(update)
      }
    },
    [isControlled, onValueChange],
  )
  // When true, attachment tokens expand to fit their full label instead of
  // truncating — toggled via the attachments menu, mirroring copilot-chat.
  const [showFullAttachmentNames, setShowFullAttachmentNames] = useState(false)
  const [isQueueModifierDown, setIsQueueModifierDown] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const textAreaRef = useRef<HTMLTextAreaElement>(null)
  const presentationSubmitRef = useRef<HTMLButtonElement>(null)
  const presentationStopRef = useRef<HTMLButtonElement>(null)
  const {containerRef, command: observeCommand} = useChatComposerInteractions(interactionObserver, textAreaRef)
  const wrapperRef = useRef<HTMLDivElement>(null)
  // Focusable element (the link, or the remove button for url-less tokens) per
  // attachment token, so removing a token can move focus to an adjacent token
  // instead of letting it fall back to <body> when the node unmounts.
  const attachmentTokenRefs = useRef<Array<HTMLElement | null>>([])
  // Label spans per token, measured to decide whether any value is truncated
  // and therefore whether to offer the "show full names" toggle.
  const attachmentLabelRefs = useRef<Array<HTMLElement | null>>([])
  const attachmentsContainerRef = useRef<HTMLDivElement>(null)
  const hasOverflowingAttachments = useAttachmentOverflow(
    attachments?.length ?? 0,
    attachmentLabelRefs,
    attachmentsContainerRef,
  )
  // Announce only the transitions — started, finished, failed — and never the
  // per-chunk percentage, which would otherwise talk over everything else.
  const announcedUploadsRef = useRef(new Map<string, 'uploading' | 'failed'>())
  useEffect(() => {
    const announced = announcedUploadsRef.current
    const seen = new Set<string>()
    for (const attachment of attachments ?? []) {
      const status = attachment.upload?.status
      if (!status) continue
      seen.add(attachment.id)
      if (announced.get(attachment.id) === status) continue
      announced.set(attachment.id, status)
      if (status === 'uploading') announce(`Uploading ${attachment.label}`)
      else
        announce(`Upload of ${attachment.label} failed: ${attachment.upload?.error ?? 'unknown error'}`, {
          assertive: true,
        })
    }
    for (const [id, status] of announced) {
      if (seen.has(id)) continue
      announced.delete(id)
      // The upload state is dropped once the file is written, so an entry that was
      // uploading and is now gone from the list has landed.
      if (status === 'uploading') announce('Upload complete')
    }
  }, [attachments])

  const toggleFullAttachmentNames = useCallback(() => {
    setShowFullAttachmentNames(prev => !prev)
  }, [])
  const removeAttachmentWithFocus = useCallback(
    (index: number, id: string) => {
      const nextFocus =
        attachmentTokenRefs.current[index + 1] ?? attachmentTokenRefs.current[index - 1] ?? textAreaRef.current
      nextFocus?.focus()
      onRemoveAttachment?.(id)
    },
    [onRemoveAttachment],
  )
  const prevHeightRef = useRef(MIN_HEIGHT)
  // When a failed submit restores the draft, we reapply the caret/selection the
  // user had before submitting. Set while restoring, consumed by the effect
  // below once the restored text has been committed to the textarea.
  const pendingSelectionRef = useRef<{start: number; end: number} | null>(null)

  const isSubmittingWhileStreaming = !readOnly && isStreaming && submitWhileStreaming && onSubmit !== undefined
  const hasSubmittableContent = shellCommandText.trim().length > 0 || hasAttachedContent || attachmentPreview != null
  const canSubmitWhenPopulated =
    !disabled &&
    !submitDisabled &&
    !readOnly &&
    !isSubmitting &&
    onSubmit !== undefined &&
    (!isStreaming || isSubmittingWhileStreaming)
  const canSubmit = hasSubmittableContent && canSubmitWhenPopulated
  const isModifierQueueEnabled = !readOnly && isStreaming && queueOnModifierEnter && onQueueSubmit !== undefined
  const canQueueWithModifier = isModifierQueueEnabled && hasSubmittableContent && !disabled && !queueSubmitDisabled
  const showModifierQueueAction = canQueueWithModifier && isQueueModifierDown
  const showSubmit = !isStreaming || isSubmittingWhileStreaming || !onStop || readOnly
  const showStop = isStreaming && Boolean(onStop) && !readOnly
  useChatComposerReadiness(
    onReadiness,
    textAreaRef,
    readOnly,
    isSubmitting,
    onSubmit !== undefined,
    canSubmitWhenPopulated,
  )
  useChatComposerPresentation(
    onPresentation,
    textAreaRef,
    presentationSubmitRef,
    presentationStopRef,
    readOnly,
    canSubmitWhenPopulated,
    hasSubmittableContent,
    showModifierQueueAction ? 'queue' : isSubmittingWhileStreaming ? 'steer' : 'send',
    showSubmit,
    showStop,
  )

  // --- Legacy JS autosize (used when `cssAutosize` is off) ---
  useEffect(() => {
    if (cssAutosize) return
    const textarea = textAreaRef.current
    const wrapper = wrapperRef.current
    if (!textarea || !wrapper) return

    // Intentional write-then-read: collapsing height to measure scrollHeight is required for auto-resize.
    textarea.style.height = '0'
    // An empty textarea's scrollHeight includes its wrapping placeholder, which must not size the composer.
    // eslint-disable-next-line @github-ui/web-vitals/no-layout-read-after-write
    const scrollHeight = text ? Math.max(textarea.scrollHeight, MIN_HEIGHT) : MIN_HEIGHT
    const maximumHeight = maxTextareaHeight ?? MAX_HEIGHT
    const height = Math.min(scrollHeight, maximumHeight)

    // Batch writes together
    textarea.style.height = `${scrollHeight}px`
    wrapper.style.maxHeight = maxTextareaHeight ? `${maxTextareaHeight}px` : ''
    if (scrollHeight <= maximumHeight) wrapper.scrollTop = 0
    if (height !== prevHeightRef.current) {
      wrapper.style.height = `${height}px`
      prevHeightRef.current = height
    }
  }, [text, cssAutosize, maxTextareaHeight])

  // --- CSS autosize (used when `cssAutosize` is on) ---
  // Disabled when CSS autosize is off so the hook performs no measurement; its styles are only applied below
  // when `cssAutosize` is true.
  const {ref: heightRef, ...heightStyles} = useDynamicTextareaHeight({disabled: !cssAutosize, value: text})

  // Focus on mount using a callback ref, also capturing the node for the legacy autosize path.
  const focusOnMountRef = useCallback(
    (node: HTMLTextAreaElement | null) => {
      if (node) {
        textAreaRef.current = node
        if (!disableAutoFocus) {
          node.focus()
        }
      }
    },
    [disableAutoFocus],
  )
  const mergedRef = useMergedRefs(heightRef, useMergedRefs(focusOnMountRef, forwardedTextAreaRef ?? null))
  const mergedSubmitButtonRef = useMergedRefs(presentationSubmitRef, submitButtonRef ?? null)

  // CSS autosize moves the horizontal padding from the textarea onto the wrapper, so forward presses on
  // that padding to the textarea. Presses on children or on the wrapper's scrollbar are left alone.
  const handleWrapperMouseDown = useCallback((event: React.MouseEvent<HTMLDivElement>) => {
    const wrapper = event.currentTarget
    if (event.target !== wrapper || event.nativeEvent.offsetX >= wrapper.clientWidth) return
    event.preventDefault()
    textAreaRef.current?.focus()
  }, [])

  const runSubmitAction = useCallback(
    async (
      action: ChatComposerSubmitHandler,
      canRun: boolean,
      kind: ComposerCommandKind,
      method: ChatComposerSubmitMethod,
      event?: Event,
    ) => {
      if (!canRun) return
      observeCommand(kind, event)
      // Clear the input immediately on submit so the message visibly "sends",
      // rather than lingering until an awaited (potentially slow) onSubmit
      // resolves. Restore it on failure so the user can retry — but only if the
      // field is still empty, to avoid clobbering anything typed in the meantime.
      const submitted = text
      const textarea = textAreaRef.current
      const selection = textarea ? {start: textarea.selectionStart, end: textarea.selectionEnd} : null
      if (clearOnSubmit) setText('', 'submit-clear')
      if (disableWhileSubmitting) setIsSubmitting(true)
      let actionSucceeded = false
      try {
        onSubmitGesture?.(method)
      } catch {
        // Observers must never block the submit.
      }
      try {
        const interactionAction = kind === 'queue' ? onQueueSubmitInteraction : onSubmitInteraction
        actionSucceeded =
          (await (interactionObserver && interactionAction
            ? interactionAction(submitted, event)
            : action(submitted))) !== false
      } catch {
        actionSucceeded = false
      } finally {
        if (disableWhileSubmitting) setIsSubmitting(false)
      }
      if (actionSucceeded) return
      if (!clearOnSubmit) return
      setText(current => {
        if (current !== '') return current
        // Only record a pending selection when there's actual text to restore.
        // For an attachment-only submit (`submitted === ''`) the text doesn't
        // change, so the effect would never consume the ref and a later edit
        // would apply a stale {0,0} selection, yanking the caret to the start.
        if (submitted !== '') {
          pendingSelectionRef.current = selection ?? {start: submitted.length, end: submitted.length}
        }
        return submitted
      }, 'submit-restore')
    },
    [
      clearOnSubmit,
      setText,
      text,
      disableWhileSubmitting,
      interactionObserver,
      observeCommand,
      onQueueSubmitInteraction,
      onSubmitInteraction,
      onSubmitGesture,
    ],
  )
  const handleSubmit = useCallback(
    (method: ChatComposerSubmitMethod, event?: Event) => {
      if (onSubmit) {
        return runSubmitAction(onSubmit, canSubmit, isSubmittingWhileStreaming ? 'steer' : 'send', method, event)
      }
    },
    [canSubmit, isSubmittingWhileStreaming, onSubmit, runSubmitAction],
  )
  const handleModifierQueue = useCallback(
    (method: ChatComposerSubmitMethod, event?: Event) => {
      if (onQueueSubmit) return runSubmitAction(onQueueSubmit, canQueueWithModifier, 'queue', method, event)
    },
    [canQueueWithModifier, onQueueSubmit, runSubmitAction],
  )
  const handlePrimaryAction = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
      const modifierPressed = isMacOS() ? event.metaKey : event.ctrlKey
      if (isModifierQueueEnabled && modifierPressed) return handleModifierQueue('button', event.nativeEvent)
      if (showModifierQueueAction) return handleModifierQueue('button', event.nativeEvent)
      if (preventSubmitKeysWhenDisabled && !canSubmit) onBlockedSubmit?.()
      return handleSubmit('button', event.nativeEvent)
    },
    [
      canSubmit,
      handleModifierQueue,
      handleSubmit,
      isModifierQueueEnabled,
      onBlockedSubmit,
      preventSubmitKeysWhenDisabled,
      showModifierQueueAction,
    ],
  )

  const updateQueueModifier = useCallback(
    (event: React.KeyboardEvent<HTMLElement>) => {
      if (event.nativeEvent.isComposing) return
      // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
      const modifierPressed = isMacOS() ? event.metaKey : event.ctrlKey
      setIsQueueModifierDown(isModifierQueueEnabled && modifierPressed)
    },
    [isModifierQueueEnabled],
  )

  useEffect(() => {
    if (!queueOnModifierEnter) return
    const resetQueueModifier = () => setIsQueueModifierDown(false)
    const handleKeyUp = (event: KeyboardEvent) => {
      // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
      if (event.key === 'Control' || event.key === 'Meta') resetQueueModifier()
    }
    const handleVisibilityChange = () => {
      if (document.visibilityState !== 'visible') resetQueueModifier()
    }

    window.addEventListener('blur', resetQueueModifier)
    window.addEventListener('keyup', handleKeyUp)
    document.addEventListener('visibilitychange', handleVisibilityChange)
    return () => {
      window.removeEventListener('blur', resetQueueModifier)
      window.removeEventListener('keyup', handleKeyUp)
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [queueOnModifierEnter])

  // Reapply the pre-submit caret/selection after a failed submit restores the
  // draft, so the user resumes editing exactly where they left off. We
  // deliberately don't call focus(): the selection persists on the (possibly
  // unfocused) textarea and is honored when the user returns to it, so an async
  // rejection can't steal focus from wherever they navigated in the meantime.
  useEffect(() => {
    const pending = pendingSelectionRef.current
    if (!pending) return
    pendingSelectionRef.current = null
    const textarea = textAreaRef.current
    if (!textarea) return
    const max = textarea.value.length
    textarea.setSelectionRange(Math.min(pending.start, max), Math.min(pending.end, max))
  }, [text])

  // Text-first: our onPaste (code detection) gets the paste event first. If it
  // consumes the paste (e.g. it looked like pasted code and was turned into a
  // block), defaultPrevented signals handlePaste to leave file handling alone.
  const handlePaste = useCallback<React.ClipboardEventHandler<HTMLTextAreaElement>>(
    event => {
      onPaste?.(event)
      if (event.defaultPrevented) return
      if (onFilesAdded) {
        const files = Array.from(event.clipboardData?.files ?? [])
        if (files.length > 0) {
          event.preventDefault()
          // Stop the paste from bubbling to any ancestor drop zone (e.g. a
          // wrapping FileAttachment) whose own paste handler would otherwise
          // re-attach the same files.
          event.stopPropagation()
          onFilesAdded(files, 'paste')
        }
      }
    },
    [onPaste, onFilesAdded],
  )

  const handleStop = useCallback(
    (event?: {nativeEvent: Event}) => {
      if (readOnly) return
      observeCommand('stop', event?.nativeEvent)
      if (interactionObserver && onStopInteraction) onStopInteraction(event?.nativeEvent)
      else onStop?.()
    },
    [onStop, onStopInteraction, readOnly, interactionObserver, observeCommand],
  )

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (event.nativeEvent.isComposing) return
      updateQueueModifier(event)

      if (
        isShellMode &&
        // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
        event.key === 'Backspace' &&
        event.currentTarget.selectionStart === 0 &&
        event.currentTarget.selectionEnd === 0
      ) {
        event.preventDefault()
        const nextText = text.slice(1)
        pendingSelectionRef.current = {start: 0, end: 0}
        setText(nextText)
        onInputMutation?.()
        if (!nextText.startsWith('!')) {
          announce('Shell mode disabled')
        }
        return
      }

      // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
      if (event.key === 'Backspace' && event.currentTarget.value === '' && onEmptyBackspace) {
        event.preventDefault()
        onEmptyBackspace()
        return
      }

      // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
      if (event.key === 'Enter' && !event.shiftKey) {
        // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
        const queueModifierPressed = isMacOS() ? event.metaKey : event.ctrlKey
        if (isModifierQueueEnabled && queueModifierPressed) {
          event.preventDefault()
          if (canQueueWithModifier) void handleModifierQueue('modifier_enter', event.nativeEvent)
          else if (preventSubmitKeysWhenDisabled) onBlockedSubmit?.()
          return
        }

        // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
        const withModifier = event.metaKey || event.ctrlKey
        if (!submitWithModifier || withModifier) {
          if (canSubmit) {
            event.preventDefault()
            handleSubmit(withModifier ? 'modifier_enter' : 'enter', event.nativeEvent)
          } else if (preventSubmitKeysWhenDisabled) {
            event.preventDefault()
            onBlockedSubmit?.()
          }
          return
        }
      }

      // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
      if (event.key === 'Escape' && isStreaming && onStop) {
        event.preventDefault()
        handleStop(event)
      }
    },
    [
      canQueueWithModifier,
      canSubmit,
      onBlockedSubmit,
      preventSubmitKeysWhenDisabled,
      handleModifierQueue,
      handleSubmit,
      handleStop,
      isModifierQueueEnabled,
      isShellMode,
      isStreaming,
      onInputMutation,
      onStop,
      onEmptyBackspace,
      setText,
      submitWithModifier,
      text,
      updateQueueModifier,
    ],
  )

  const handleInputKeyUp = useCallback(
    (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
      updateQueueModifier(event)
    },
    [updateQueueModifier],
  )

  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLTextAreaElement>) => {
      const nextText = event.target.value
      const nextIsShellMode = supportsShellCommands && nextText.startsWith('!')
      if (!isShellMode && nextIsShellMode) {
        announce('Shell mode enabled')
      } else if (isShellMode && !nextIsShellMode) {
        announce('Shell mode disabled')
      }
      setText(nextText)
      onInputMutation?.()
    },
    [isShellMode, onInputMutation, setText, supportsShellCommands],
  )

  return (
    <div
      ref={containerRef}
      className={clsx(
        styles.Container,
        containerClassName,
        isSubmitting && styles.ContainerBusy,
        isShellMode && styles.ContainerShellMode,
      )}
      aria-busy={isSubmitting || undefined}
      data-testid="chat-composer-container"
    >
      {attachments && attachments.length > 0 && (
        <div className={styles.Attachments} data-testid="chat-composer-attachments" ref={attachmentsContainerRef}>
          {attachments.map((a, index) => {
            // Resolve the icon key once so the glyph, color, and status label all
            // agree — otherwise an omitted `icon` renders the default file glyph
            // with no accessible label, leaving the type conveyed by the
            // aria-hidden glyph alone.
            const iconKey = a.icon ?? 'file'
            const uploadFailed = a.upload?.status === 'failed'
            const Icon = uploadFailed ? AlertIcon : (ATTACHMENT_ICONS[iconKey] ?? FileIcon)
            const iconColorClass = uploadFailed ? styles.AttachmentIconFailed : ATTACHMENT_ICON_COLORS[iconKey]
            const statusLabel = ATTACHMENT_ICON_STATUS_LABELS[iconKey]
            const removeButton = !readOnly && a.removable !== false && onRemoveAttachment && (
              <IconButton
                icon={XIcon}
                aria-label={`Remove ${a.label}`}
                variant="invisible"
                size="small"
                className={styles.AttachmentRemove}
                onClick={event => {
                  event.preventDefault()
                  event.stopPropagation()
                  removeAttachmentWithFocus(index, a.id)
                }}
              />
            )
            const content = (
              <>
                <Icon size="small" className={clsx(styles.AttachmentIcon, iconColorClass)} />
                {/* The octicon is aria-hidden, so surface the status (conveyed
                    visually by glyph + color) as visually-hidden text. */}
                {statusLabel && <VisuallyHidden>{`${statusLabel}: `}</VisuallyHidden>}
                <span
                  className={styles.AttachmentLabel}
                  ref={el => {
                    attachmentLabelRefs.current[index] = el
                  }}
                >
                  {a.label}
                </span>
                {a.upload?.status === 'uploading' && (
                  // A progressbar rather than live text: the percentage changes on every
                  // acknowledged chunk, and announcing each one would bury everything else
                  // the composer has to say. Status transitions are announced separately.
                  <span
                    className={styles.AttachmentProgress}
                    role="progressbar"
                    aria-label={`Uploading ${a.label}`}
                    aria-valuemin={0}
                    aria-valuemax={100}
                    aria-valuenow={a.upload.percent}
                    style={{'--attachment-progress': (a.upload.percent ?? 0) / 100}}
                  />
                )}
                {a.upload?.status === 'failed' && (
                  <span className={styles.AttachmentFailed}>
                    <VisuallyHidden>{`Upload failed: ${a.upload.error ?? 'unknown error'}`}</VisuallyHidden>
                    <span aria-hidden="true">Failed</span>
                  </span>
                )}
              </>
            )

            const retryButton = a.upload?.status === 'failed' && a.upload.onRetry && (
              <IconButton
                icon={SyncIcon}
                aria-label={`Retry upload of ${a.label}`}
                variant="invisible"
                size="small"
                className={styles.AttachmentRemove}
                onClick={event => {
                  event.preventDefault()
                  event.stopPropagation()
                  a.upload?.onRetry?.()
                }}
              />
            )
            const tokenActions = (
              <>
                {retryButton}
                {removeButton}
              </>
            )

            // For url-backed tokens the link and remove button are rendered as
            // siblings inside the token — nesting the IconButton (a <button>)
            // inside the <a> would be invalid HTML and break keyboard/screen-
            // reader interaction.
            const tokenClassName = clsx(
              styles.AttachmentToken,
              showFullAttachmentNames && styles.AttachmentTokenFullWidth,
              uploadFailed && styles.AttachmentTokenFailed,
            )
            return a.url ? (
              <span key={a.id} className={tokenClassName}>
                <a
                  className={styles.AttachmentLink}
                  href={a.url}
                  onClick={event => {
                    // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic -- Keep native modified-link navigation.
                    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return
                    onAttachmentClick?.(a, event)
                  }}
                  target="_blank"
                  rel="noopener noreferrer"
                  ref={el => {
                    attachmentTokenRefs.current[index] = el
                  }}
                  onKeyDown={
                    removeButton
                      ? event => {
                          // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
                          if (event.key === 'Delete' || event.key === 'Backspace') {
                            event.preventDefault()
                            removeAttachmentWithFocus(index, a.id)
                          }
                        }
                      : undefined
                  }
                >
                  {content}
                </a>
                {tokenActions}
              </span>
            ) : onAttachmentClick ? (
              <span key={a.id} className={tokenClassName}>
                <button
                  type="button"
                  className={styles.AttachmentLink}
                  ref={el => {
                    attachmentTokenRefs.current[index] = el
                  }}
                  onClick={event => onAttachmentClick(a, event)}
                  onKeyDown={
                    removeButton
                      ? event => {
                          // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
                          if (event.key === 'Delete' || event.key === 'Backspace') {
                            event.preventDefault()
                            removeAttachmentWithFocus(index, a.id)
                          }
                        }
                      : undefined
                  }
                >
                  {content}
                </button>
                {tokenActions}
              </span>
            ) : (
              <span
                key={a.id}
                className={tokenClassName}
                ref={el => {
                  // Url-less tokens have no link, so track the remove button as
                  // the token's focusable element for post-removal focus moves.
                  attachmentTokenRefs.current[index] = el?.querySelector('button') ?? null
                }}
              >
                {content}
                {tokenActions}
              </span>
            )
          })}
          {hasOverflowingAttachments && (
            <ActionMenu>
              <ActionMenu.Anchor>
                <IconButton
                  icon={KebabHorizontalIcon}
                  aria-label="Attachment options"
                  variant="invisible"
                  size="small"
                  tooltipDirection="n"
                  className={styles.AttachmentMenuButton}
                />
              </ActionMenu.Anchor>
              <ActionMenu.Overlay width="small">
                <ActionList>
                  <ActionList.Item onSelect={toggleFullAttachmentNames}>
                    <ActionList.LeadingVisual>
                      {showFullAttachmentNames ? <EyeClosedIcon /> : <EyeIcon />}
                    </ActionList.LeadingVisual>
                    <span>{showFullAttachmentNames ? 'Hide full attachment names' : 'Show full attachment names'}</span>
                  </ActionList.Item>
                </ActionList>
              </ActionMenu.Overlay>
            </ActionMenu>
          )}
        </div>
      )}
      {attachmentPreview}
      {aboveInput}
      {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions -- pointer-only shortcut; keyboard users reach the textarea directly */}
      <div
        className={clsx(styles.InputWrapper, cssAutosize && styles.InputWrapperCssAutosize)}
        ref={cssAutosize ? undefined : wrapperRef}
        style={maxTextareaHeight ? {maxHeight: maxTextareaHeight} : undefined}
        onMouseDown={cssAutosize ? handleWrapperMouseDown : undefined}
        data-testid="chat-composer-input-wrapper"
      >
        {(() => {
          const textareaEl = (
            <textarea
              ref={mergedRef}
              className={clsx(
                styles.Input,
                cssAutosize && styles.InputCssAutosize,
                inputTextSize === 'responsive' && styles.responsiveText,
              )}
              style={cssAutosize ? heightStyles : undefined}
              value={text}
              onChange={readOnly ? undefined : handleChange}
              onKeyDown={readOnly ? undefined : handleKeyDown}
              onKeyUp={readOnly ? undefined : handleInputKeyUp}
              onPaste={!readOnly && (onPaste || onFilesAdded) ? handlePaste : undefined}
              placeholder={isShellMode ? 'Enter shell command' : placeholder}
              aria-label={isShellMode ? 'Shell command' : (ariaLabel ?? placeholder)}
              aria-describedby={ariaDescribedBy}
              aria-readonly={readOnly || isSubmitting}
              aria-multiline="true"
              readOnly={readOnly || isSubmitting}
              rows={1}
              {...(maxLength !== undefined ? {maxLength} : {})}
              data-copilot-chat-input
              data-testid="chat-composer-input"
              data-react-autofocus={autoFocusOnNavigation || undefined}
            />
          )
          return renderInput ? renderInput(textareaEl) : textareaEl
        })()}
      </div>
      <div className={styles.Toolbar}>
        <div className={styles.ToolbarLeading} data-testid="chat-composer-toolbar-leading">
          {isShellMode && <span className={styles.ShellModeLabel}>Shell</span>}
          {!isShellMode && readOnly && showAttachButtonWhenReadOnly && (
            <IconButton
              icon={PlusIcon}
              aria-label="Attach"
              variant="invisible"
              size="medium"
              className={styles.AttachButton}
              disabled
              {...(tooltipDirection ? {tooltipDirection} : {})}
              data-testid="chat-composer-attach-menu"
            />
          )}
          {!isShellMode &&
            !readOnly &&
            (onAttachFiles || onUploadFiles || onAttachRepo || Boolean(attachMenuContent)) && (
              <ActionMenu anchorRef={attachButtonRef}>
                <ActionMenu.Anchor>
                  <IconButton
                    icon={PlusIcon}
                    aria-label="Attach"
                    variant="invisible"
                    size="medium"
                    className={styles.AttachButton}
                    {...(tooltipDirection ? {tooltipDirection} : {})}
                    data-testid="chat-composer-attach-menu"
                  />
                </ActionMenu.Anchor>
                <ActionMenu.Overlay
                  side={attachMenuSide ?? (portalContainerName ? 'outside-top' : undefined)}
                  align="start"
                  portalContainerName={portalContainerName}
                >
                  <ActionList>
                    {onAttachFiles && (
                      <ActionList.Item onSelect={onAttachFiles}>
                        <ActionList.LeadingVisual>
                          <FileCodeIcon />
                        </ActionList.LeadingVisual>
                        Files and folders
                      </ActionList.Item>
                    )}
                    {onUploadFiles && (
                      <ActionList.Item onSelect={onUploadFiles}>
                        <ActionList.LeadingVisual>
                          <UploadIcon />
                        </ActionList.LeadingVisual>
                        Upload from computer
                      </ActionList.Item>
                    )}
                    {onAttachRepo && (
                      <>
                        <ActionList.Divider />
                        <ActionList.Item onSelect={onAttachRepo}>
                          <ActionList.LeadingVisual>
                            <RepoIcon />
                          </ActionList.LeadingVisual>
                          Repository
                        </ActionList.Item>
                      </>
                    )}
                    {attachMenuContent}
                  </ActionList>
                </ActionMenu.Overlay>
              </ActionMenu>
            )}
          {!isShellMode && toolbarContent}
        </div>
        <div className={styles.ToolbarActions}>
          {trailingToolbarContent ? (
            <>
              {trailingToolbarContent}
              {showTrailingToolbarSeparator ? (
                <div
                  className={styles.ToolbarSeparator}
                  aria-hidden="true"
                  data-testid="chat-composer-toolbar-separator"
                />
              ) : null}
            </>
          ) : null}
          {showSubmit && (
            <IconButton
              ref={mergedSubmitButtonRef}
              icon={showModifierQueueAction ? QueuedMessageIcon : PaperAirplaneIcon}
              aria-label={
                showModifierQueueAction
                  ? queueSubmitAriaLabel
                  : isSubmittingWhileStreaming
                    ? submitWhileStreamingAriaLabel
                    : 'Send message'
              }
              variant="invisible"
              size="medium"
              loading={isSubmitting}
              loadingAnnouncement="Submitting"
              description={showModifierQueueAction ? undefined : submitDescription}
              onClick={handlePrimaryAction}
              onKeyDown={updateQueueModifier}
              onKeyUp={updateQueueModifier}
              aria-disabled={showModifierQueueAction ? !canQueueWithModifier : !canSubmit}
              data-testid="chat-composer-submit"
              {...(tooltipDirection ? {tooltipDirection} : {})}
            />
          )}
          {showStop && (
            <IconButton
              ref={presentationStopRef}
              icon={SquareFillIcon}
              aria-label="Stop generating"
              variant="invisible"
              size="medium"
              className={styles.StopButton}
              onClick={handleStop}
              data-testid="chat-composer-stop"
              {...(tooltipDirection ? {tooltipDirection} : {})}
            />
          )}
        </div>
      </div>
    </div>
  )
}
